/* eslint-disable no-param-reassign */
import { getParent } from "mobx-state-tree";
import BaseChatModel from "./base";
import { DateTime } from "luxon";
import newMessage from "assets/audio/message.mp3";
import Channel from "controllers/channel";
import Timer from "controllers/timer";
import { notification } from "antd";

const DESTROY_INACTIVE_CHAT_TIMEOUT =
  process.env.REACT_APP_LIVE_CHAT_AUTO_DESTROY_TIMEOUT * 60 * 1000;

const LiveChat = BaseChatModel.named("Chat")
  .volatile((self) => ({
    newMessageAudio: new Audio(newMessage),
    beDestroyedAt: null,
    destroyTimer: new Timer({
      timeout: DESTROY_INACTIVE_CHAT_TIMEOUT,
      callback: () => self.destroy(),
    }),
  }))
  .actions((self) => {
    const _baseAddNewMessage = self.addNewMessage;

    return {
      beforeDestroy() {
        self.destroyTimer.dispose();
        self.controller.dispose();
        self.controller = null;
      },

      afterCreate() {
        try {
          self.controller = new Channel(
            {
              channelName: "RealtimeChat::ChatChannel",
              params: { id: self.id, session: self.session },
            },
            {
              received: self._handleChannelReceived,
              connected: self.start,
              disconnected: self.stop,
            }
          );
        } catch (error) {
          console.log("error", error);
        }
      },

      addNewMessage(message) {
        _baseAddNewMessage(message);
        if (message.user.id) self.newMessageAudio?.play();
      },

      start() {
        self.sendConnectedStatus();
        self.startedAt = new Date();
        self.isOnline = true;
        self.beDestroyedAt = null;
        self.destroyTimer.stop();
        return self;
      },

      send(data) {
        if (
          !self.controller.perform("message_from_consultant", {
            ...data,
            source: self.type,
          })
        ) {
          notification["error"]({
            message: `Message has not been send`,
          });
        }
      },

      stop() {
        self.setTypingStatus(false);
        self.beDestroyedAt = DateTime.local()
          .plus({
            minutes: process.env.REACT_APP_LIVE_CHAT_AUTO_DESTROY_TIMEOUT,
          })
          .toJSDate();
        self.destroyTimer.start();
        self.isOnline = false;
        return self;
      },

      sendConnectedStatus() {
        self.controller.perform("consultant_connected");
      },

      _handleChannelReceived(data) {
        const { action, message } = data;
        if (action === "new_message") self.addNewMessage(message);
        if (action === "initial_messages")
          self.setInitialMessages(message.reverse());
        if (action === "contact_typing") self.setTypingStatus(message.typing);
        if (action === "pay_minute") self.incrementPaidMinutes();
      },

      startTyping() {
        self.controller.perform("consultant_typing", { typing: true });
      },

      stopTyping() {
        self.controller.perform("consultant_typing", { typing: false });
      },

      destroy() {
        const chats = getParent(self, 2); // link to repository
        return chats.remove(self.id);
      },

      incrementPaidMinutes() {
        self.paidMinutes += 1;
        return self;
      },
    };
  });

export default LiveChat;
